import { useEffect, useRef, useState } from 'react';
import { Columns3 } from 'lucide-react';
import {
  DEFAULT_IN_SHOP_COLUMNS,
  IN_SHOP_COLUMNS,
  IN_SHOP_COLUMN_LABELS,
  type InShopColumn,
} from '@mustauto/types/in-shop';

/**
 * A lista de colunas, com lugar e placa fixos no topo. Usada no botão
 * "Colunas" da tela e na janela de exportação — é a mesma escolha.
 */
export function InShopColumnChecklist({
  columns,
  onChange,
  idPrefix,
  className,
}: {
  columns: readonly InShopColumn[];
  onChange: (next: InShopColumn[]) => void;
  idPrefix: string;
  className?: string;
}) {
  const selected = new Set(columns);
  const toggle = (c: InShopColumn) =>
    onChange(selected.has(c) ? columns.filter((x) => x !== c) : [...columns, c]);

  return (
    <div className={className}>
      {['Onde está', 'Placa'].map((label) => (
        <label key={label} className="flex items-center gap-2.5 py-1.5 text-sm text-muted-foreground">
          <input type="checkbox" checked disabled className="h-4 w-4 accent-primary" />
          {label}
          <span className="ml-auto text-xs">sempre</span>
        </label>
      ))}
      {IN_SHOP_COLUMNS.map((c) => (
        <label key={c} htmlFor={`${idPrefix}-${c}`} className="flex cursor-pointer items-center gap-2.5 py-1.5 text-sm">
          <input
            id={`${idPrefix}-${c}`}
            type="checkbox"
            checked={selected.has(c)}
            onChange={() => toggle(c)}
            className="h-4 w-4 accent-primary"
          />
          {IN_SHOP_COLUMN_LABELS[c]}
        </label>
      ))}
    </div>
  );
}

/** Botão "Colunas" da tela: escolhe o que aparece na lista e na exportação. */
export function InShopColumnsPicker({
  columns,
  onChange,
}: {
  columns: readonly InShopColumn[];
  onChange: (next: InShopColumn[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex h-9 items-center gap-2 rounded-lg border border-input bg-card px-3 text-sm font-medium hover:bg-muted"
      >
        <Columns3 size={16} />
        Colunas ({columns.length + 2})
      </button>
      {open && (
        <div className="absolute left-0 top-full z-20 mt-1 w-72 rounded-lg border border-border bg-card shadow-lg">
          <div className="border-b border-border px-4 py-3">
            <p className="text-sm font-semibold">Colunas</p>
            <p className="text-xs text-muted-foreground">Valem para a tela, o PDF e a planilha.</p>
          </div>
          <InShopColumnChecklist columns={columns} onChange={onChange} idPrefix="in-shop-col" className="px-4 py-2" />
          <div className="flex items-center justify-between border-t border-border px-4 py-2.5">
            <button
              type="button"
              className="text-xs font-semibold text-primary hover:underline"
              onClick={() => onChange([...DEFAULT_IN_SHOP_COLUMNS])}
            >
              Voltar ao padrão
            </button>
            <button
              type="button"
              className="rounded-md bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground"
              onClick={() => setOpen(false)}
            >
              Pronto
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
